import { useSyncExternalStore } from "react"

import type { AgentOrbColors } from "./orb-math"
import { agentOrbColorsFromAccent, DEFAULT_AGENT_ORB_COLORS } from "./orb-math"

// The dashboard writes the accent into a <style> tag and the widget onto <html>,
// so one observer watches both and every orb on the page shares it.
const listeners = new Set<() => void>()
let observer: MutationObserver | null = null
let current: AgentOrbColors = DEFAULT_AGENT_ORB_COLORS
let currentAccent: string | null = null

function read() {
	const accent = getComputedStyle(document.documentElement)
		.getPropertyValue("--portal-accent-color")
		.trim()
	if (accent === currentAccent) return false
	currentAccent = accent
	current = agentOrbColorsFromAccent(accent)
	return true
}

function subscribe(listener: () => void) {
	listeners.add(listener)
	if (!observer) {
		read()
		observer = new MutationObserver(() => {
			if (read()) for (const l of listeners) l()
		})
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["style", "class"],
		})
		observer.observe(document.head, {
			childList: true,
			subtree: true,
			characterData: true,
		})
	}
	return () => {
		listeners.delete(listener)
		if (listeners.size === 0) {
			observer?.disconnect()
			observer = null
		}
	}
}

function getSnapshot() {
	if (!observer) read()
	return current
}

/** The orb in the portal's accent, following dark mode and a synced accent change. */
export function useAccentOrbColors(): AgentOrbColors {
	return useSyncExternalStore(
		subscribe,
		getSnapshot,
		() => DEFAULT_AGENT_ORB_COLORS,
	)
}
